import { createAnimationLegacy } from '../common/schemas/legacy/animation';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';
import { createShadowSettingsLegacy } from '../common/schemas/legacy/shadow';

import type { ImageEntity, LegacyImage, LegacyImageDefault, LegacyImageZero } from './schemas';
import { createImageStylesLegacy } from './schemas';

/**
 * Which legacy placement the factory should produce — corresponds to the
 * envelope's `type` discriminator.
 *  - `'default'` (or omitted): an image dropped into a normal column. The
 *    persisted envelope omits the `type` field entirely for byte-identical
 *    parity with pre-discriminator data on disk. Data carries the simple
 *    per-device position grid and `isSynchronizeStyle: true`.
 *  - `'zero'`: an image dropped into a zero block. Envelope carries
 *    `type: 'zero'`; data uses the extended-position grid (350×250 default),
 *    adds the `imagePosition` focal-point keyword, and `isSynchronizeStyle`
 *    starts `false` (zero-block widgets are always per-device).
 *
 * The `'specific'` placement (registered in the host model's `params.type`)
 * shares the `'default'` shape — only the editor chrome differs.
 */
export type LegacyImageVariant = 'default' | 'zero';

const createDefaultData = (): LegacyImageDefault['data'] => ({
    image: null,
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    isBorder: false,
    isIndependentCorners: false,
    resizeType: 'scale',
    isSynchronizeStyle: true,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    animation: createAnimationLegacy(),
    position: createWidgetPositionLegacy(),
    isShadow: false,
    shadowSettings: createShadowSettingsLegacy(),
});

const createZeroData = (): LegacyImageZero['data'] => ({
    image: null,
    horizontalAlign: 'L',
    mobileHorizontalAlign: 'L',
    tabletHorizontalAlign: 'L',
    isBorder: false,
    isIndependentCorners: false,
    resizeType: 'scale',
    isSynchronizeStyle: false,
    hideOnDesktop: false,
    hideOnTablet: false,
    hideOnMobile: false,
    animation: createAnimationLegacy(),
    position: createExtendedPositionLegacy({
        width: 350,
        tabletWidth: 350,
        mobileWidth: 350,
        height: 250,
        tabletHeight: 250,
        mobileHeight: 250,
    }),
    imagePosition: 'center',
    isShadow: false,
    shadowSettings: createShadowSettingsLegacy(),
});

/**
 * Factory for the current legacy image entity — matches the persisted widget
 * JSON shape: `{ id, name: 'image', columnId, data, styles, … }`. Variant
 * selects between default-column placement (no `type` field, simple position
 * grid) and zero-block placement (`type: 'zero'`, extended-position grid +
 * `imagePosition`). The structured `styles` group is identical for both.
 */
export function createLegacyImage(variant?: 'default'): LegacyImageDefault;
export function createLegacyImage(variant: 'zero'): LegacyImageZero;
export function createLegacyImage(variant: LegacyImageVariant = 'default'): LegacyImage {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'image',
            columnId: '',
            type: 'zero',
            data: createZeroData(),
            styles: createImageStylesLegacy(),
        };
    }

    return {
        id: '',
        name: 'image',
        columnId: '',
        data: createDefaultData(),
        styles: createImageStylesLegacy(),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new image widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyImage}; pass the variant to opt into the zero-block tag.
 */
export function createImage(variant?: 'default'): LegacyImageDefault;
export function createImage(variant: 'zero'): LegacyImageZero;
export function createImage(variant?: LegacyImageVariant): ImageEntity {
    // `as any` because the overload signatures resolve at the call site; the
    // implementation just forwards the variant.
    return createLegacyImage(variant as any);
}
